import React, { useMemo } from "react";
import { formatMoney } from "../../../../../features/utils/formatMoney";
import { getRefundAdminGuidance } from "../../../../../data/flight/order/getRefundAdminGuidance";

function PricingBreakdown({ data }) {
  const travellerLib = {
    ADT: "Adult",
    CHD: "Child",
    CNN: "Child",
    INF: "Infant",
    INS: "Infant",
  };

  const passengerPrice = Array.isArray(data?.passengerPrice)
    ? data.passengerPrice
    : [];

  const travellers = data?.flightBooking?.travelers || [];

  const cachedAdmin = useMemo(() => {
    try {
      return JSON.parse(
        sessionStorage.getItem(`refundQuoteAdmin:${data?._id}`) || "null",
      );
    } catch (_) {
      return null;
    }
  }, [data?._id]);

  const rows = useMemo(() => {
    return passengerPrice.map((p, idx) => {
      let count = 1;
      const passengerIds = data?.passengerIds || [];

      const travellerTypePassegers = travellers?.filter(
        (item) =>
          item?.travelerType === p?.travelerType &&
          passengerIds?.includes(item?.id),
      );
      count = Math.max(count, travellerTypePassegers?.length);

      const breakdown = Array.isArray(p?.breakDown) ? p.breakDown : [];
      const cachePax = (cachedAdmin?.passengerPrice || []).find(
        (c) => c?.travelerType === p?.travelerType,
      );
      const sourceRows = breakdown.length
        ? breakdown
        : cachePax?.breakDown || [];

      // Pair airline (isDeducted:false) with adjusted (isDeducted:true) by name
      const names = [
        ...new Set(sourceRows.map((b) => b?.name).filter(Boolean)),
      ];
      const lines = names.map((name) => {
        const same = sourceRows.filter((b) => b?.name === name);
        const airlineRow = same.find((b) => b?.isDeducted === false);
        const sellingRow = same.find((b) => b?.isDeducted !== false);
        const selling = Number(
          sellingRow?.amount ?? airlineRow?.amount ?? 0,
        );
        const airline = Number(
          airlineRow?.amount ??
            sellingRow?.airlineAmount ??
            selling,
        );
        return {
          name,
          selling,
          airline,
          markup: selling - airline,
        };
      });

      const subtotal = lines.reduce((sum, item) => sum + item.selling, 0) * count;

      const travellerTypeLabel =
        travellerLib[p?.travelerType] || p?.travelerType || "Adult";
      return { key: idx, travellerTypeLabel, lines, subtotal, count };
    });
  }, [passengerPrice, travellers, data?.passengerIds, cachedAdmin]);

  const serviceFee = (data?.intraverseFee ?? 0) || 0;
  const supplierRefund = Number(
    data?.supplierRefundAmount || cachedAdmin?.supplierRefundAmount || 0,
  );
  const refundMarkup = Number(
    data?.intraverseRefundMarkup || cachedAdmin?.intraverseRefundMarkup || 0,
  );
  const hasAutoRefundSplit =
    supplierRefund > 0 || Math.abs(refundMarkup) > 0;
  const hasLineAirlineSplit = rows.some((row) =>
    row.lines.some((l) => Math.abs(l.markup) > 0.001 || l.airline !== l.selling),
  );

  return (
    <div className="w-full flex flex-col">
      {hasAutoRefundSplit ? (
        <div className="mb-4 space-y-2 rounded-lg border border-[#E8EEF5] bg-white p-3">
          {supplierRefund > 0 ? (
            <div className="flex justify-between items-center text-sm">
              <div>
                <div className="font-medium text-[#0F172A]">
                  Supplier refund
                </div>
                <div className="text-[10px] text-[#6D7C94]">
                  Amount from airline / supplier
                </div>
              </div>
              <span className="font-semibold text-[#0F172A]">
                {formatMoney(supplierRefund)}
              </span>
            </div>
          ) : null}
          {Math.abs(refundMarkup) > 0 ? (
            <div className="flex justify-between items-center text-sm">
              <div>
                <div className="font-medium text-[#0F172A]">
                  Intraverse refund markup
                </div>
                <div className="text-[10px] text-[#6D7C94]">
                  Extra kept on deductions (platform)
                </div>
              </div>
              <span className="font-semibold text-emerald-700">
                {formatMoney(refundMarkup)}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {rows.map((row) => (
        <div key={row.key} className="mb-4 last:mb-0">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium">
              {row.count} x {row.travellerTypeLabel}
            </span>
            <span className="font-semibold text-gray-900">
              {formatMoney(row.subtotal)}
            </span>
          </div>

          <div className="pl-4 space-y-2">
            {row.lines.map((b, i) => (
              <div key={i} className="space-y-0.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{b?.name}</span>
                  <span className="text-gray-900 font-medium">
                    {formatMoney(b.selling)}
                  </span>
                </div>
                {hasLineAirlineSplit || Math.abs(b.markup) > 0.001 ? (
                  <div className="flex justify-between text-[11px] text-[#6D7C94] pl-1">
                    <span>
                      Airline {formatMoney(b.airline)}
                      {Math.abs(b.markup) > 0.001
                        ? ` · markup ${b.markup > 0 ? "+" : ""}${formatMoney(b.markup)}`
                        : " · at cost"}
                    </span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex justify-between items-center mt-4 mb-2 rounded-lg bg-[#FFFBEB] border border-[#FEF3C7] px-3 py-2">
        <div>
          <span className="font-medium text-[#92400E]">
            Intraverse service fee
          </span>
          <div className="text-[10px] text-[#B45309]">
            From fee config (`intraverseFee`)
          </div>
        </div>
        <span className="font-semibold text-[#92400E]">
          {formatMoney(serviceFee)}
        </span>
      </div>

      <hr className="border-gray-300 mb-3" />

      <div className="flex justify-between items-center mb-1">
        <span className="font-semibold text-gray-900">
          {hasAutoRefundSplit || hasLineAirlineSplit
            ? "Refund to agent (payable)"
            : "Total"}
        </span>
        <span className="font-semibold text-gray-900">
          {formatMoney(data?.payable || 0)}
        </span>
      </div>

      <div className="text-right mt-1 text-primary1 font-semibold text-lg">
        - {formatMoney(data?.payable || 0)}
      </div>
    </div>
  );
}

function PricingStatusBanner({ data, children }) {
  const { statusLabel, tone } = getRefundAdminGuidance(data);
  const className =
    tone === "success"
      ? "bg-green-100 text-green-700"
      : tone === "danger"
        ? "bg-red-100 text-red-700"
        : tone === "wait"
          ? "bg-amber-100 text-amber-800"
          : "bg-[#FE9F29]/10 text-[#FE9F29]";

  return (
    <span className={`${className} text-sm font-semibold px-3 py-1 rounded-md`}>
      {children || statusLabel}
    </span>
  );
}

export default PricingBreakdown;
export { PricingStatusBanner };
